import { useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { useAssignAsset, useInternalUsersList } from "../hooks/useAssetRegistry";
import type { Asset } from "../types";
import { extractError } from "../utils";
import { SearchableSelect } from "./SearchableSelect";

interface Props {
  open: boolean;
  onClose: () => void;
  asset: Asset | null;
}

export function AssignAssetModal({ open, onClose, asset }: Props) {
  const [employee, setEmployee] = useState("");
  const [handoverDate, setHandoverDate] = useState("");
  const [error, setError] = useState("");
  const { data: users = [], isLoading } = useInternalUsersList(open);
  const assign = useAssignAsset();

  useEffect(() => {
    if (open) {
      setEmployee("");
      setHandoverDate("");
      setError("");
    }
  }, [open]);

  const title = useMemo(() => (asset ? `Przekaż: ${asset.name}` : "Przekaż sprzęt"), [asset]);
  const options = useMemo(() => users.map((u) => ({ value: u.id, label: u.name })), [users]);

  function submit() {
    if (!asset) return;
    if (!employee) {
      setError("Wybierz pracownika.");
      return;
    }
    assign.mutate(
      { id: asset.id, data: { employee, ...(handoverDate ? { handover_date: handoverDate } : {}) } },
      { onSuccess: () => onClose(), onError: (e) => setError(extractError(e)) },
    );
  }

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
            Pracownik *
          </label>
          <SearchableSelect
            options={options}
            value={employee}
            onChange={setEmployee}
            loading={isLoading}
            placeholder="— wybierz pracownika —"
          />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
            Data przekazania
          </label>
          <DatePicker
            value={handoverDate}
            onChange={setHandoverDate}
          />
          <p className="text-xs text-[var(--color-text-muted)] mt-1">
            Jeśli nie podasz daty, zostanie ustawiona dzisiejsza.
          </p>
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={submit} loading={assign.isPending}>
            Przekaż
          </Button>
        </div>
      </div>
    </Modal>
  );
}
